import React, { useEffect, useRef, useState } from "react"
import { StyleSheet, Text, View } from "react-native"
import { connect } from "react-redux"
import * as amplitude from "@amplitude/analytics-react-native"
import { useApiStatus } from "@common/hooks/useApiStatus"
import FilledButtonM from "@common/components/button/FilledButtonM"
import ClassChipGridM from "@common/components/funnel/ClassChipGridM"
import {
    FUNNEL_TITLE_FONT_SIZE,
    FUNNEL_TITLE_LINE_HEIGHT,
} from "@common/components/funnel/FunnelQuestionScreen"
import FunnelStepHeader from "@common/components/funnel/FunnelStepHeader"
import FunnelStepLayout from "@common/components/funnel/FunnelStepLayout"
import { FUNNEL_SCENES } from "@common/components/funnel/funnelScenes"
import { theme } from "@common/theme/theme"
import { apiRequest } from "@data/redux/actions/api"
import { acquisitionStudentClasses } from "@data/constants/formConstants"
import SummerHelpBanner from "../summer/components/SummerHelpBanner"
import { useInscriptionFunnel } from "./context/InscriptionFunnelContext"
import { useInscriptionFunnelGuard } from "./hooks/useInscriptionFunnelGuard"
import { inscriptionFunnelService } from "./services/inscriptionFunnelService"
import {
    INSCRIPTION_QUESTIONS,
    INSCRIPTION_STEPS,
    normaliseClass,
} from "./constants/inscriptionContent"

/**
 * Étape 1 du funnel Inscription — UNE SEULE question : la classe.
 *
 * C'est la divergence assumée avec l'été, qui en pose deux (classe actuelle +
 * classe de l'année suivante). Ce funnel vend un accompagnement dans l'année en
 * cours, où seule la classe actuelle importe.
 *
 * Une scène plutôt qu'une photo : l'élève et ses objets — le même gabarit que
 * le stage, pour que le parent qui vient de la LP retrouve le même produit.
 */
const InscriptionClassScreen = (props) => {
    const { navigation, route } = props
    const { state, update, reset } = useInscriptionFunnel()
    const { nextParams } = useInscriptionFunnelGuard({ isStart: true })

    const [studentClass, setStudentClass] = useState(state.studentClass || null)

    // Hydratation depuis la LP : soit un submissionGUID HubSpot (le backend
    // relit la soumission et pré-remplit), soit des query params directs.
    useEffect(() => {
        const params = route?.params || {}
        const submissionGUID =
            params.submissionGUID ||
            (typeof window !== "undefined"
                ? new URLSearchParams(window.location?.search || "").get(
                      "submissionGUID"
                  )
                : null)

        // `?format_step=` sur le lien d'entrée force une disposition (recette),
        // par les deux portes ; le serveur écarte alors ce dossier de la
        // mesure, et ignore une valeur qu'il ne connaît pas.
        const formatStep = params.format_step

        if (submissionGUID && !state.funnelStateId) {
            props.apiRequest(
                inscriptionFunnelService.initInscriptionFromHubSpot,
                {},
                { submissionGUID, formatStep }
            )
            return
        }

        const klass = normaliseClass(params.class)

        // Un e-mail différent de celui en mémoire = un AUTRE lead dans le même
        // onglet (un commercial enchaîne deux familles). Sans cette remise à
        // zéro, le second parcours repart avec les réponses du premier.
        const isOtherLead =
            !!params.email &&
            !!state.parentEmail &&
            params.email.trim().toLowerCase() !==
                String(state.parentEmail).trim().toLowerCase()

        if (isOtherLead) {
            reset()
            setStudentClass(klass || null)
        }

        if (klass && !studentClass) setStudentClass(klass)
        if (params.firstname && !state.studentFirstname) {
            update({ studentFirstname: params.firstname })
        }

        // Entrée directe depuis la LP (sans HubSpot) : l'état est créé dès
        // qu'on a de quoi le faire — sans email il n'y a pas de lead à créer.
        // On lit `isOtherLead`, PAS `state.funnelStateId` : `reset()` est un
        // setState, l'état lu ici porte encore le lead précédent.
        const alreadyStarted = !isOtherLead && !!state.funnelStateId

        if (params.email && params.firstname && klass && !alreadyStarted) {
            props.apiRequest(
                inscriptionFunnelService.startInscriptionFunnel,
                {},
                {
                    email: params.email,
                    firstname: params.firstname,
                    studentClass: klass,
                    formatStep,
                }
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // L'ARRIVÉE sur le funnel — le dénominateur de tout calcul de drop. Émise
    // au montage sans attendre `funnelStateId` : un prospect qui repart avant
    // sa réponse est précisément le décrochage qu'on cherche à mesurer.
    const startedTracked = useRef(false)
    useEffect(() => {
        if (startedTracked.current) return
        startedTracked.current = true
        amplitude.track("inscription_funnel_started", {
            funnel_state_id: state.funnelStateId ?? null,
            student_class:
                normaliseClass(route?.params?.class) ||
                state.studentClass ||
                null,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * L'identité du lead est posée dès l'entrée, sur les DEUX portes : l'écran
     * de paiement fabrique son jeton depuis cet e-mail quand l'URL ne porte pas
     * `?user=`, et la garde s'en sert pour dériver le jeton dès la 1ʳᵉ étape.
     */
    const captureLeadIdentity = (data) => {
        const email = data?.user?.email
        if (email) update({ parentEmail: email })
        if (data?.user?.firstname) {
            update({ studentFirstname: data.user.firstname })
        }
        if (data?.format_step) update({ formatStep: data.format_step })
        if (data?.funnel_state_id) {
            update({ funnelStateId: data.funnel_state_id })
            // L'arrivée est partie avant que cet identifiant n'existe : en
            // propriété utilisateur, elle reste joignable au reste du parcours.
            const identifyObj = new amplitude.Identify()
            identifyObj.set("funnel_state_id", data.funnel_state_id)
            amplitude.identify(identifyObj)
        }
    }

    useApiStatus(
        inscriptionFunnelService.startInscriptionFunnel,
        null,
        false,
        captureLeadIdentity
    )
    useApiStatus(
        inscriptionFunnelService.initInscriptionFromHubSpot,
        null,
        false,
        (data) => {
            captureLeadIdentity(data)
            const klass = normaliseClass(data?.user?.student_class)
            if (klass) setStudentClass(klass)
        }
    )

    const submit = () => {
        update({ studentClass })

        const stateId = state.funnelStateId
        if (stateId) {
            props.apiRequest(
                inscriptionFunnelService.updateInscriptionAnswers,
                { stateId },
                { studentClass }
            )
        }

        amplitude.track("inscription_funnel_class_completed", {
            funnel_state_id: stateId,
            student_class: studentClass,
        })

        navigation.navigate("inscription/diagnostic", nextParams)
    }

    const firstname = state.studentFirstname || "votre enfant"
    const canContinue = !!studentClass

    return (
        <FunnelStepLayout
            scene={FUNNEL_SCENES.classe}
            contentContainerStyle={styles.content}
            header={
                <FunnelStepHeader
                    step={INSCRIPTION_STEPS.class}
                    total={INSCRIPTION_STEPS.total}
                    hideBack
                />
            }
            footer={
                <View style={styles.footer}>
                    <FilledButtonM
                        label="Continuer"
                        onPress={submit}
                        disabled={!canContinue}
                        color={
                            canContinue
                                ? theme.colors.primary
                                : theme.colors.lightMore
                        }
                        style={{ width: "100%", marginBottom: 0 }}
                    />
                </View>
            }
        >
            <Text style={styles.title}>
                {INSCRIPTION_QUESTIONS.class(firstname)}
            </Text>
            <ClassChipGridM
                options={acquisitionStudentClasses.map((c) => ({ id: c, label: c }))}
                selectedIds={studentClass ? [studentClass] : []}
                onToggle={(id) => setStudentClass(id)}
            />
            <SummerHelpBanner
                step={INSCRIPTION_STEPS.class}
                trackingEvent="inscription_funnel_help_clicked"
            />
        </FunnelStepLayout>
    )
}

const styles = StyleSheet.create({
    content: { paddingBottom: 28 },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 14,
    },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
})

export default connect(null, { apiRequest })(InscriptionClassScreen)
